import React, { useState, useEffect, KeyboardEvent } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import Paginate from 'components/paginate/Paginate';

import Card from 'components/card/Card';

import { apiGetCustomerList } from 'api';

import type { APIPaginatedResponse, APICustomerItem } from 'types/api.types';

import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  Input,
  IconButton,
  Tooltip,
  Button,
  Collapse,
  Stack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Spinner,
} from '@chakra-ui/react';

import { SearchIcon, SmallCloseIcon } from '@chakra-ui/icons';

import { useUrlFilters } from 'hooks/useUrlFilters';

const CUSTOMER_FILTER_KEYS = ['search', 'phone', 'email'] as const;

const CustomersListView = () => {
  const navigate = useNavigate();
  const { getFilter, setFilters, page, setPage, resetAll, searchParams } =
    useUrlFilters(CUSTOMER_FILTER_KEYS);

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [nameSearch, setNameSearch] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');

  useEffect(() => {
    setNameSearch(searchParams.get('search') ?? '');
    setPhoneSearch(searchParams.get('phone') ?? '');
    setEmailSearch(searchParams.get('email') ?? '');
  }, [searchParams]);

  const filters = React.useMemo(
    () => ({
      ...(getFilter('search') && { search: getFilter('search') }),
      ...(getFilter('phone') && { phone: getFilter('phone') }),
      ...(getFilter('email') && { email: getFilter('email') }),
    }),
    [getFilter, searchParams],
  );

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');
  const hoverBgColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const { data, isLoading, error } = useQuery<
    APIPaginatedResponse<APICustomerItem>
  >({
    queryKey: ['customers', page, filters],
    queryFn: () => apiGetCustomerList(page + 1, filters),
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearch = () => {
    setFilters({
      search: nameSearch.trim(),
      phone: phoneSearch.trim(),
      email: emailSearch.trim(),
    });
  };

  const resetSearch = () => {
    setNameSearch('');
    setPhoneSearch('');
    setEmailSearch('');
    resetAll();
  };

  const onCustomerClick = (customer: APICustomerItem) => {
    navigate(`/admin/customers/${customer.id}`);
  };

  if (error) {
    return (
      <Card>
        <Box p="20px" pb="0">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{JSON.stringify(error)}</AlertDescription>
          </Alert>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Customers
        </Text>
        <Flex gap={2}>
          <Tooltip
            label={showSearchBar ? 'Hide search' : 'Show search'}
            hasArrow
          >
            <IconButton
              aria-label={showSearchBar ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              variant={showSearchBar ? 'solid' : 'ghost'}
              colorScheme={showSearchBar ? 'yellow' : 'gray'}
              onClick={() => setShowSearchBar(!showSearchBar)}
              size="md"
            />
          </Tooltip>
        </Flex>
      </Flex>

      <Collapse in={showSearchBar} animateOpacity>
        <Box
          px="20px"
          pb="20px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box flex="1">
              <Input
                variant="main"
                placeholder="Search by name or address"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              />
            </Box>

            <Box flex="1">
              <Input
                variant="main"
                placeholder="Search by email"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              />
            </Box>

            <Box flex="1">
              <Input
                variant="main"
                placeholder="Search by phone"
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              />
            </Box>

            <Flex gap={2}>
              <Button
                onClick={handleSearch}
                leftIcon={<SearchIcon />}
                size="md"
                variant="brand"
              >
                Search
              </Button>
              <Button
                onClick={resetSearch}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
              >
                Reset
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Collapse>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            <Th pe="10px" borderColor={borderColor} color="gray.400">
              Client Name
            </Th>
            <Th pe="10px" borderColor={borderColor} color="gray.400">
              Client Address
            </Th>
            <Th pe="10px" borderColor={borderColor} color="gray.400">
              Client Email
            </Th>
            <Th pe="10px" borderColor={borderColor} color="gray.400">
              Client Phone Number
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={4} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading customers...
                </Flex>
              </Td>
            </Tr>
          )}

          {data?.results.map((customer) => (
            <Tr
              key={customer.id}
              cursor="pointer"
              _hover={{
                bg: hoverBgColor,
              }}
              onClick={() => onCustomerClick(customer)}
            >
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {customer.first_name} {customer.last_name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {customer.full_address || customer.address || '-'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {customer.email || '-'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {customer.phone_number || '-'}
                  </Text>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {data && data.results.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No customers found
        </Box>
      )}

      <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />
    </Card>
  );
};

export default CustomersListView;
